import { useRef, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { kebabCase } from 'lodash';

import { useLazyRequest } from '~hooks/index';
import { getItemsInProgressToExport } from '~services/admin/report';
import { AdminReportItemsInProgressFilters } from '~services/admin/report/types';
import { formatterDate } from '~utils/formatter';
import { downloadFileByRequest } from '~utils/download';
import { Nullable } from '~globals/types/commons';
import { ReportItemsInProgressMode } from '~globals/types/enums';

interface UseExportItemsInProgressParams {
  onSuccess?: () => void;
  onError?: () => void;
}

interface UseExportItemsInProgressReturn {
  loading: boolean;
  onExportItemsInProgress: (
    params: AdminReportItemsInProgressFilters,
  ) => Promise<void>;
}

const getPefixByMode = (mode: Nullable<ReportItemsInProgressMode>): string => {
  let prefix = 'por';

  if (mode === ReportItemsInProgressMode.Drivers) prefix += ' choferes';
  if (mode === ReportItemsInProgressMode.Zones) prefix += ' zonas';
  if (mode === ReportItemsInProgressMode.Companies) prefix += ' compañias';

  return prefix;
};

const getDefaultFileName = (prefix: string): string => {
  const currentDate = formatterDate(new Date(), { format: 'YYYYMMDD' });

  return `seguimiento-${prefix}-${currentDate}.xlsx`;
};

export const useExportItemsInProgress = (
  options?: UseExportItemsInProgressParams,
): UseExportItemsInProgressReturn => {
  const currentReportMode = useRef<Nullable<ReportItemsInProgressMode>>(null);
  const { enqueueSnackbar } = useSnackbar();

  const [, loadingExportItemsInProgress, , executeExportItemsInProgress] =
    useLazyRequest({
      request: getItemsInProgressToExport,
      withPostSuccess: (response) => {
        const prefix = getPefixByMode(currentReportMode.current);

        if (response.headers && response.data) {
          const defaultFileName = getDefaultFileName(kebabCase(prefix));

          options?.onSuccess?.();

          downloadFileByRequest(
            response.headers,
            response.data,
            defaultFileName,
          );

          enqueueSnackbar(`Se ha descargado el seguimiento ${prefix}`, {
            variant: 'success',
          });
        } else {
          enqueueSnackbar(
            `No se pudo descargar el seguimiento ${prefix}, intente nuevamente`,
            { variant: 'error' },
          );
        }
      },
      withPostFailure: () => {
        const prefix = getPefixByMode(currentReportMode.current);

        options?.onError?.();

        enqueueSnackbar(
          `No se pudo descargar el seguimiento ${prefix}, intente nuevamente`,
          { variant: 'error' },
        );
      },
    });

  const onExportItemsInProgress = useCallback(
    async (params: AdminReportItemsInProgressFilters) => {
      currentReportMode.current = params.reportItemsInProgressMode;
      await executeExportItemsInProgress(params);
      currentReportMode.current = null;
    },
    [executeExportItemsInProgress],
  );

  return { loading: loadingExportItemsInProgress, onExportItemsInProgress };
};
